import { FlexTypography } from "@lyfegen-platform/react-common";
import { Box, Collapse, Typography } from "@material-ui/core";
import { Fragment, Key, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EMPTY_VALUE_PLACEHOLDER } from "../../../models/common/data";
import startUpper from "../../../utils/start-upper";
import useStyles from "../use-styles";
import { Record } from "./RecordsCard";
import { getTreatmentCodeByCountry } from "@lyfegen-platform/utils";
import TreatmentPriceColumns from "./TreatmentPriceColumns";

export type Treatment = {
	brandName?: string;
	genericName?: string;
	details: Array<TreatmentDetails>;
};

export type TreatmentDetails = {
	pack?: string;
	basePrice?: string;
	publicPrice?: number;
	exFactoryPrice?: number;
	code?: string;
};

export interface ITreatmentSummaryDetails {
	indications?: Array<string>;
	diseases?: Array<string>;
	hasMultipleBasePrices?: boolean;
	drugQuantity?: number;
	treatments?: Array<Treatment>;
	isCollapsed: boolean;
	currencyName: string;
	records: Array<Record>;
}

const hiddenTreatment = {
	brandName: "XX",
	genericName: "XX",
	details: [
		{
			pack: "XX",
			basePrice: "XX",
			code: "XX",
			publicPrice: "XX",
			exFactoryPrice: "XX",
		},
	],
};

const isTreatmentInRecords = (treatmentDetails: TreatmentDetails, records: Array<Record>) =>
	records.reduce(
		(acc, curr) => acc || Boolean(curr[1].find((r) => getTreatmentCodeByCountry(r.treatment) === treatmentDetails.code)),
		false
	);

export function TreatmentSummaryDetails(props: ITreatmentSummaryDetails) {
	const { t } = useTranslation();
	const { fw400, smallItem, cellRightAlignment } = useStyles();

	const columns = [
		{ name: "preparations-brandName" },
		{ name: "generic-name" },
		{ name: "packSize", numericColumn: "true" },
		{ name: "productId", numericColumn: "true" },
	];

	const pricingColumns = [
		{ name: "ex-factory-price", numericColumn: "true" },
		{ name: "public-price", numericColumn: "true" },
	];

	const allColumns = props.hasMultipleBasePrices
		? [...columns, ...pricingColumns]
		: [...columns, { name: "base-price", numericColumn: "true" }];
	const indications = props.indications?.join(", ") || EMPTY_VALUE_PLACEHOLDER;
	const diseases = props.diseases?.join(", ") || EMPTY_VALUE_PLACEHOLDER;

	const usedTreatments = useMemo(
		() =>
			props.treatments?.flatMap(({ brandName, details }) =>
				details.flatMap((treatment) => (isTreatmentInRecords(treatment, props.records) ? { brandName, code: treatment.code } : []))
			),
		[props.treatments?.length]
	);

	return (
		<Box width="100%" px={4}>
			<Box display="flex" width="100%" p={1}>
				<Box width="24%" overflow="hidden">
					<Typography noWrap variant="subtitle2" className={fw400} color="textSecondary">
						{startUpper(t("indication"))}
					</Typography>
					<FlexTypography text={indications} width={100} />
				</Box>
				<Box width="24%" overflow="hidden">
					<Typography noWrap variant="subtitle2" className={fw400} color="textSecondary">
						{startUpper(t("disease"))}
					</Typography>
					<FlexTypography text={diseases} width={100} />
				</Box>
			</Box>
			<Collapse in={!props.isCollapsed}>
				<Box display="flex" width="100%" p={1}>
					<table style={{ width: "100%" }} cellSpacing="0">
						<thead>
							<tr>
								{allColumns.map((c, i) => (
									<td key={i} style={{ width: i === 0 ? "24%" : "", textAlign: c.numericColumn ? "right" : "left" }}>
										<Typography noWrap variant="subtitle2" className={fw400} color="textSecondary">
											{startUpper(t(c.name)) || EMPTY_VALUE_PLACEHOLDER}
										</Typography>
									</td>
								))}
							</tr>
						</thead>
						<tbody>
							{props.treatments &&
								(props.treatments.length ? props.treatments : Array(props.drugQuantity || 1).fill(hiddenTreatment)).map(
									(treatment, i) => (
										<tr key={i} style={{ width: "100%", background: i % 2 === 1 ? "#f5f8f9" : undefined }}>
											<td className={smallItem}>
												<Typography
													style={{
														color: usedTreatments?.find((current) => treatment.brandName === current.brandName)
															? "#212121"
															: "#969392",
													}}
													noWrap
												>
													{treatment.brandName || EMPTY_VALUE_PLACEHOLDER}
												</Typography>
											</td>
											<td className={smallItem}>
												<Typography
													style={{
														color: usedTreatments?.find((current) => treatment.brandName === current.brandName)
															? "#212121"
															: "#969392",
													}}
													noWrap
												>
													{treatment.genericName || EMPTY_VALUE_PLACEHOLDER}
												</Typography>
											</td>
											<td className={`${smallItem}, ${cellRightAlignment}`}>
												{treatment.details?.length &&
													treatment.details.map((details: TreatmentDetails, di?: Key) => (
														<Fragment key={di}>
															<Typography
																style={{
																	color: usedTreatments?.find((current) => details.code === current.code)
																		? "#212121"
																		: "#969392",
																}}
																noWrap
															>
																{details.pack || EMPTY_VALUE_PLACEHOLDER}
															</Typography>
														</Fragment>
													))}
											</td>
											<td className={`${smallItem}, ${cellRightAlignment}`}>
												{treatment.details?.length &&
													treatment.details.map((details: TreatmentDetails, di?: Key) => (
														<Fragment key={di}>
															<Typography
																style={{
																	color: usedTreatments?.find((current) => details.code === current.code)
																		? "#212121"
																		: "#969392",
																}}
																noWrap
															>
																{details.code || EMPTY_VALUE_PLACEHOLDER}
															</Typography>
														</Fragment>
													))}
											</td>
											<TreatmentPriceColumns
												hasMultipleBasePrices={props.hasMultipleBasePrices}
												treatment={treatment}
												usedTreatments={usedTreatments}
												currencyName={props.currencyName}
											/>
										</tr>
									)
								)}
						</tbody>
					</table>
				</Box>
			</Collapse>
		</Box>
	);
}
